// @noflow

/* eslint-disable i18next/no-literal-string */
import { SpringValue, useSpring } from '@react-spring/web'
import { useCallback, useRef, useState } from 'react'
import type { SyntheticEvent } from 'react'

type Args = {
  handleOnClose: () => void
}

type UseDragBehavior = {
  onTouchStart: (event: SyntheticEvent) => void
  onTouchMove: (event: SyntheticEvent) => void
  onTouchEnd: () => void
  dragStyles: {
    transform: SpringValue<string>
  }
}

const ANIMATION_CONFIG = {
  tension: 300,
  friction: 30
}

const DRAG = {
  CLOSE_THRESHOLD: 80, // px to trigger close
  MAX_STRETCH_UP: 20, // max px to stretch when pulling up
  STRETCH_FACTOR: 0.05, // how much to resist stretching (lower = more resistance)
  PULL_DOWN_FACTOR: 0.8, // how much to follow finger when pulling down
  SCALE_DIVISOR: 250 // controls how pronounced the scale effect is
}

/**
 * Use drag behavior hook
 *
 * This hook is used to add native-feeling drag behavior to mobile drawers
 * - Stretches when pulled up
 * - Translates down when pulled down
 * - Auto-closes when pulled down past threshold
 *
 * @param {Function} handleOnClose - The function to call when the drawer should close
 * @returns {UseDragBehavior} - The drag behavior object
 */
const useDragBehavior = ({ handleOnClose }: Args): UseDragBehavior => {
  const [touchStart, setTouchStart] = useState<number | null>(null)
  const [touchCurrent, setTouchCurrent] = useState<number | null>(null)
  const isDragging = useRef(false)

  const [{ transform }, api] = useSpring(() => ({
    transform: 'translateY(0px) scaleY(1)',
    config: ANIMATION_CONFIG
  }))

  /**
   * Handle the start of a touch event
   * Captures the initial touch position and sets dragging state
   */
  const onTouchStart = useCallback((event) => {
    const [{ clientY }] = event.targetTouches

    setTouchStart(clientY)
    setTouchCurrent(clientY)
    isDragging.current = true
  }, [])

  /**
   * Handles touch movement and applies appropriate transforms
   * - When pulling up: applies resistance and scaling effect
   * - When pulling down: follows finger with slight resistance
   */
  const onTouchMove = useCallback(
    (event) => {
      if (!touchStart || !isDragging.current) return

      const [{ clientY }] = event.targetTouches
      setTouchCurrent(clientY)

      const deltaY = clientY - touchStart

      if (deltaY < 0) {
        // Pulling UP: Apply stretch resistance and scaling
        const stretchAmount = Math.max(
          deltaY * DRAG.STRETCH_FACTOR,
          -DRAG.MAX_STRETCH_UP
        )
        const scaleYAmount = 1 + Math.abs(stretchAmount) / DRAG.SCALE_DIVISOR

        api.start({
          transform: `translateY(${stretchAmount}px) scaleY(${scaleYAmount})`,
          immediate: true
        })
      } else {
        // Pulling DOWN: Follow finger with resistance
        const translateAmount = deltaY * DRAG.PULL_DOWN_FACTOR

        api.start({
          transform: `translateY(${translateAmount}px) scaleY(1)`,
          immediate: true
        })
      }
    },
    [touchStart, api]
  )

  /**
   * Handles the end of a touch event
   * - If pulled down past threshold: closes the drawer
   * - Otherwise: resets to original position with animation
   */
  const onTouchEnd = useCallback(() => {
    if (!touchStart || !touchCurrent || !isDragging.current) return

    const deltaY = touchCurrent - touchStart

    if (deltaY > DRAG.CLOSE_THRESHOLD) {
      handleOnClose()
    } else {
      api.start({
        transform: 'translateY(0px) scaleY(1)',
        immediate: false
      })
    }

    // Reset all touch tracking state
    isDragging.current = false
    setTouchStart(null)
    setTouchCurrent(null)
  }, [touchStart, touchCurrent, handleOnClose, api])

  return {
    onTouchStart,
    onTouchMove,
    onTouchEnd,
    dragStyles: {
      transform
    }
  }
}

export { useDragBehavior }
